import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  Wand2,
  Library as LibraryIcon,
  FolderKanban,
  Gamepad2,
  Mic,
  Zap,
  Clapperboard,
  Megaphone,
  CreditCard,
  KeyRound,
  BookText,
  LogOut,
  Coins,
  Menu,
  Music4,
  BookOpenText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { Logo, LogoFull, APP_NAME } from "@/components/Logo";
import SupportChat from "@/components/SupportChat";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const nav = [
  { to: "/dashboard", label: "Studio", icon: Wand2, end: true, testId: "nav-studio" },
  { to: "/dashboard/library", label: "Library", icon: LibraryIcon, testId: "nav-library" },
  { to: "/dashboard/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
  { to: "/dashboard/gaming", label: "Adaptive Gaming", icon: Gamepad2, testId: "nav-gaming" },
  { to: "/dashboard/themes", label: "Themes & leitmotifs", icon: Music4, testId: "nav-themes" },
  { to: "/dashboard/narrative", label: "NarrativeSync", icon: BookOpenText, testId: "nav-narrative" },
  { to: "/dashboard/voice", label: "Voix & SFX", icon: Mic, testId: "nav-voice" },
  { to: "/dashboard/synth", label: "Surge XT Synthé", icon: Zap, testId: "nav-synth" },
  { to: "/dashboard/billing", label: "Billing & Credits", icon: CreditCard, testId: "nav-billing" },
  { to: "/dashboard/api-keys", label: "API Keys", icon: KeyRound, testId: "nav-api-keys" },
  { to: "/dashboard/docs", label: "SDK & API Docs", icon: BookText, testId: "nav-docs" },
  { to: "/dashboard/promo", label: "Vidéo promo", icon: Clapperboard, testId: "nav-promo", adminOnly: true },
  { to: "/dashboard/social", label: "Réseaux sociaux", icon: Megaphone, testId: "nav-social", adminOnly: true },
];

function SidebarContent({ user, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-border flex justify-center">
        <Link to="/" aria-label={APP_NAME}><LogoFull width={190} /></Link>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {nav.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={item.testId}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent"
              }`
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border space-y-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 border border-border" data-testid="credits-display">
          <Coins size={16} className="text-primary" />
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Credits</div>
            <div className="font-mono font-bold text-sm">{user?.credits ?? 0}</div>
          </div>
          <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-primary/15 text-primary">{user?.plan}</span>
        </div>
        <div className="flex items-center gap-2 px-1">
          <div className="w-8 h-8 rounded-full bg-accent/30 flex items-center justify-center text-xs font-bold overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || "U")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onLogout} data-testid="logout-button" className="text-muted-foreground hover:text-destructive">
            <LogOut size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useSeo({ title: "Dashboard", noindex: true });

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border flex-col fixed h-screen glass z-20">
        <SidebarContent user={user} onLogout={doLogout} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 glass border-b border-border flex items-center justify-between px-4 h-14">
        <Logo iconSize={22} textClassName="text-lg" />
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono px-2 py-1 rounded bg-muted flex items-center gap-1"><Coins size={12} className="text-primary" />{user?.credits ?? 0}</span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu size={22} /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 glass border-border">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SidebarContent user={user} onLogout={doLogout} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <main className="flex-1 lg:ml-64 grid-bg min-h-screen">
        <Outlet />
      </main>

      <SupportChat />
    </div>
  );
}
